import React, { useState, useEffect } from'react';
import { getSocket } from'../../services/socket';

const DriverAccepted = ({ driver, acceptedFare, otpCode, booking }) => {
 const [driverLocation, setDriverLocation] = useState(null);
 const [isTrackingDriver, setIsTrackingDriver] = useState(false);

 // Track driver location via Socket.IO
 useEffect(() => {
 if (!driver || !driver._id) return;

 const socket = getSocket();
 if (!socket) {
 console.warn('⚠️ Socket not available for driver tracking');
 return;
 }

 console.log('📍 Starting driver location tracking for driver:', driver._id);
 setIsTrackingDriver(true);

 // Listen for driver location updates
 socket.on('driver-location-update', (data) => {
 if (data.driverId === driver._id || data.driverId === driver.id) {
 console.log('📍 Driver location update received:', data);
 setDriverLocation({
 latitude: data.latitude,
 longitude: data.longitude,
 timestamp: data.timestamp || new Date()
 });
 }
 });

 // Request initial driver location
 socket.emit('request-driver-location', { driverId: driver._id });

 // Cleanup
 return () => {
 console.log('📍 Stopping driver location tracking');
 socket.off('driver-location-update');
 setIsTrackingDriver(false);
 };
 }, [driver]);

 // Handle View in Map button click
 const handleViewInMap = () => {
 // Priority: pickupLocation (Google Places address) > pickupLocationName (pincode search)
 const pickupLocation = booking?.pickupLocation || booking?.pickupLocationName;
 const vehicleType = booking?.vehicleType || driver?.vehicleType ||'';
 
 // Map vehicle type to Google Maps travel mode
 const getTravelMode = (vehicleType) => {
 if (!vehicleType) return'driving';
 const normalizedType = vehicleType.toLowerCase().trim();
 if (normalizedType ==='bike') return'bicycling';
 if (normalizedType ==='car'|| normalizedType ==='auto'|| normalizedType ==='other') return'driving';
 return'driving';
 };
 
 const travelMode = getTravelMode(vehicleType);
 
 // Build Google Maps URL
 let googleMapsUrl;
 
 if (driverLocation && pickupLocation) {
 // Show route from driver's current location to pickup
 const driverLatLng =`${driverLocation.latitude},${driverLocation.longitude}`;
 const encodedPickupLocation = encodeURIComponent(pickupLocation);
 googleMapsUrl =`https://www.google.com/maps/dir/?api=1&origin=${driverLatLng}&destination=${encodedPickupLocation}&travelmode=${travelMode}`;
 console.log('🗺️ Opening Google Maps with driver location:', {
 driverLocation: driverLatLng,
 pickupLocation: pickupLocation,
 travelMode: travelMode
 });
 } else if (pickupLocation) {
 // Show just the pickup location
 const encodedPickupLocation = encodeURIComponent(pickupLocation);
 googleMapsUrl =`https://www.google.com/maps/search/?api=1&query=${encodedPickupLocation}`;
 console.log('🗺️ Opening Google Maps with pickup location only:', pickupLocation);
 } else {
 console.error('❌ No location data available');
 alert('Location data is not available');
 return;
 }
 
 // Navigate to Google Maps
 window.location.href = googleMapsUrl;
 };

 return (
 <div className="bg-white text-center hidden md:block">
 {/* Status Image - Bigger Size */}
 <div className="w-full h-80 md:h-96 lg:h-[500px] bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center overflow-hidden">
 <img 
 src="/rideaccepted.png"alt="Ride Accepted"className="w-full h-full object-contain"/>
 </div>
 
 <div className="p-6 md:p-8 border-b border-gray-100">
 <div className="flex flex-col items-center">
 <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-2">
 Booking Accepted
 </h2>
 <p className="text-gray-600 text-sm md:text-base mb-3">
 Your ride has been confirmed by {driver?.name ||'Driver'}
 </p>
 
 {/* Display Accepted Fare */}
 {acceptedFare && (
 <div className="bg-gray-50 px-6 py-3 rounded-lg mb-3 border border-gray-100">
 <p className="text-gray-600 text-sm mb-1">Accepted Fare</p>
 <p className="text-gray-900 font-semibold text-2xl">₹{acceptedFare}</p>
 </div>
 )}
 
 {/* Display OTP - Enhanced */}
 {/* CRITICAL: Only show OTP for reserve bookings when driver has arrived (status: driver_arrived) */}
 {/* For regular bookings, show OTP immediately when accepted */}
 {otpCode && (() => {
 // CRITICAL: Safely extract OTP code to prevent React error
 // Helper function to safely extract OTP code from string or object
 const extractOtpCode = (otp) => {
 if (!otp) return null;
 // If it's already a string/number, return it
 if (typeof otp ==='string'|| typeof otp ==='number') {
 return String(otp);
 }
 // If it's an object, extract the code property
 if (typeof otp ==='object'&& otp !== null) {
 return otp.code ? String(otp.code) : null;
 }
 return null;
 };
 
 const otpValue = extractOtpCode(otpCode);
 if (!otpValue) return null; // Don't render if OTP can't be extracted
 
 const isReserveBooking = booking?.bookingType ==='reserve';
 
 // For reserve bookings, only show OTP if driver has arrived
 if (isReserveBooking && booking?.status !=='driver_arrived') {
 return null; // Don't show OTP for reserve bookings until driver arrives
 }
 
 // Show OTP
 return (
 <div className="bg-gray-50 rounded-lg px-8 py-6 mb-3 max-w-sm w-full border border-gray-200 notranslate"translate="no">
 <div className="flex items-center justify-center mb-3 notranslate"translate="no">
 <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center notranslate"translate="no">
 <svg className="w-7 h-7 text-gray-700"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
 </svg>
 </div>
 </div>
 <p className="text-gray-600 text-sm font-semibold mb-2 uppercase tracking-wide notranslate"translate="no">Your Pickup OTP</p>
 <p 
 className="text-gray-900 font-black text-4xl tracking-widest mb-2 notranslate"translate="no"lang="en"data-translate="no"style={{ 
 unicodeBidi:'isolate', 
 direction:'ltr',
 userSelect:'none',
 WebkitUserSelect:'none',
 MozUserSelect:'none',
 msUserSelect:'none'}}
 suppressContentEditableWarning={true}
 contentEditable={false}
 >
 {String(otpValue).split('').map((digit, idx) => (
 <span key={idx} className="notranslate"translate="no"lang="en"data-translate="no"style={{ unicodeBidi:'isolate', direction:'ltr'}}>{digit}</span>
 ))}
 </p>
 <p className="text-gray-500 text-xs font-medium mt-3 flex items-center justify-center notranslate"translate="no">
 <svg className="w-4 h-4 mr-1"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
 </svg>
 Share this with the driver at pickup
 </p>
 </div>
 );
 })()}

 {/* View in Map Button */}
 <button
 onClick={handleViewInMap}
 className="w-full max-w-sm px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-base transition-colors flex items-center justify-center space-x-3 shadow-sm hover:shadow-md mt-4">
 <svg className="w-5 h-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 18.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V5.618a1 1 0 00-.553-.894L15 2m0 13V2m0 0L9 7"/>
 </svg>
 <span>Track Driver</span>
 {isTrackingDriver && driverLocation && (
 <span className="inline-flex items-center">
 <span className="animate-pulse w-2 h-2 bg-green-400 rounded-full ml-2"></span>
 </span>
 )}
 </button>

 {/* Driver Location Status */}
 {isTrackingDriver && driverLocation && (
 <p className="text-xs text-gray-500 mt-2 flex items-center">
 <svg className="w-4 h-4 mr-1 text-green-500"fill="currentColor"viewBox="0 0 20 20">
 <path fillRule="evenodd"d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"clipRule="evenodd"/>
 </svg>
 Driver location is being tracked
 </p>
 )}
 </div>
 </div>
 </div>
 );
};

export default DriverAccepted;

